import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useApi } from "../contexts/ApiProvider";
import { useFlash } from "../contexts/FlashProvider";
import {
  fallbackServices,
  getServiceImageUrl,
  normalizeService,
} from "../helpers/serviceFallbacks";

export default function ServicesPage() {
  const api = useApi();
  const flash = useFlash();
  const [services, setServices] = useState(fallbackServices);
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState("Tous");

  useEffect(() => {
    (async () => {
      const response = await api.get("/api/services/search");
      if (response.success && response?.services?.length) {
        setServices(response.services.map(normalizeService));
      } else if (response.error) {
        flash(response.error.message || "Services indisponibles", "warning");
      }
    })();
  }, [api, flash]);

  const tags = useMemo(() => {
    const allTags = services.flatMap((service) => service.tags || []);
    return ["Tous", ...Array.from(new Set(allTags)).slice(0, 8)];
  }, [services]);

  const filteredServices = useMemo(() => {
    return services.filter((service) => {
      const matchesQuery = [service.title, service.subtitle, service.description]
        .join(" ")
        .toLowerCase()
        .includes(query.toLowerCase());
      const matchesTag =
        activeTag === "Tous" || service.tags?.includes(activeTag);

      return matchesQuery && matchesTag;
    });
  }, [activeTag, query, services]);

  return (
    <div>
      <section className="py-7 py-lg-10 bg-light">
        <div className="container">
          <div className="row align-items-end g-5">
            <div className="col-12 col-lg-7">
              <p className="section-kicker mb-3">Tous nos services</p>
              <h1 className="display-4 fw-bold mb-4">
                Trouvez l'accompagnement adapté à votre besoin.
              </h1>
              <p className="lead text-muted mb-0">
                Formation, assistance, conseil, informatique et services
                opérationnels: comparez les offres, puis décrivez votre
                contexte pour recevoir une orientation claire.
              </p>
            </div>
            <div className="col-12 col-lg-5">
              <div className="content-panel p-3">
                <label className="form-label small fw-bold text-uppercase text-muted">
                  Rechercher un service
                </label>
                <input
                  className="form-control"
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Formation, santé, conseil..."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container py-7">
        <div className="d-flex flex-wrap gap-2 mb-5">
          {tags.map((tag) => (
            <button
              className={`btn btn-sm ${
                activeTag === tag ? "btn-success" : "btn-outline-dark"
              }`}
              key={tag}
              type="button"
              onClick={() => setActiveTag(tag)}
            >
              {tag}
            </button>
          ))}
        </div>

        <div className="row g-4">
          {filteredServices.map((service) => (
            <div className="col-12 col-md-6 col-xl-4" key={service._id || service.slug}>
              <article className="service-card">
                <div className="service-card-media">
                  <img src={getServiceImageUrl(service)} alt={service.title} />
                </div>
                <div className="p-4 p-lg-5">
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {service.tags.map((tag) => (
                      <span className="service-tag" key={`${service.slug}-${tag}`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h2 className="h4 fw-bold mb-3">{service.title}</h2>
                  <p className="text-muted mb-4">{service.description}</p>
                  <Link
                    className="fw-bold text-decoration-none"
                    to={`/services/${service.slug}`}
                  >
                    Explorer l'offre <span aria-hidden="true">&rarr;</span>
                  </Link>
                </div>
              </article>
            </div>
          ))}
        </div>

        {filteredServices.length === 0 && (
          <div className="content-panel p-5 text-center">
            <h2 className="h4 fw-bold">Aucun service trouvé</h2>
            <p className="text-muted mb-0">
              Essayez un autre mot-clé ou contactez-nous pour décrire votre
              besoin.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
